import { Icon } from '@amelio/ui-library';

import { IconCircle } from './surfaces';

import type { CSSProperties, ReactNode } from 'react';

import {
    MEASUREMENT_SPINE,
    MEASUREMENT_SPINE_HEAD,
    MEASUREMENT_SPINE_HEAD_ICON,
    MEASUREMENT_SPINE_LEAD_IN,
} from '@/components/objectives/objectiveStyles';

interface MeasurementSpineHeadProps {
    children: ReactNode;
    /** The form's tinted gradient or the detail page's vision tint -- the two pages differ only
     * in this fill, the rest of the card's geometry is shared. */
    surfaceClassName: string;
    dataTestId: string;
}

/**
 * The rail's own head card, "Objectif principal": the target icon, the caller's title block, and
 * the lead-in connector down to the first measurement card's dot. Its geometry is documented in
 * `objectiveStyles.ts` -- shared by the objective form and the detail page.
 */
export function MeasurementSpineHead({
    children,
    surfaceClassName,
    dataTestId,
}: MeasurementSpineHeadProps) {
    return (
        <div
            className={`${MEASUREMENT_SPINE_HEAD} ${surfaceClassName}`}
            data-testid={dataTestId}
        >
            <IconCircle className={MEASUREMENT_SPINE_HEAD_ICON}>
                <Icon
                    name={'cryptocompany-target'}
                    size={28}
                    colorClassName={'text-obj-accent'}
                    strokeWidth={1.7}
                />
            </IconCircle>

            {children}

            <div
                className={MEASUREMENT_SPINE_LEAD_IN}
                aria-hidden
                data-testid={`${dataTestId}-lead-in`}
            />
        </div>
    );
}

/**
 * The dashed purple rail the measurement cards hang off, shared by the objective form and the
 * detail page.
 */
export function MeasurementSpine({ children }: { children: ReactNode }) {
    return <div className={MEASUREMENT_SPINE}>{children}</div>;
}

interface MeasurementSpineItemProps {
    children: ReactNode;
    /**
     * How far down the card its dot sits, in pixels: the form's cards start lower than the detail
     * page's. The last card also ends the rail at this height, so the two can never disagree.
     */
    markerTop: number;
    /**
     * Ends the rail on this card. Stated by the caller rather than read off the DOM: a `last:`
     * variant only matches the container's last child, so anything the page appends after the
     * cards -- the form's "Ajouter un élément de mesure" button -- silently handed the rail back
     * to the last card.
     */
    isLast?: boolean;
}

/**
 * One card hung off the rail, with the dot-and-stub connecting it. Each card draws its segment of
 * the rail down to the next card, and the last one stops it at the centre of its own dot. The
 * segment, the indent it leaves for the card and the dot's position all come from
 * `.obj-spine-item` / `.obj-spine-dot` in `app.css`, derived there from the rail's single abscissa
 * and this card's `markerTop` -- which is also where the whole rail is folded away below 768px.
 */
export function MeasurementSpineItem({
    children,
    markerTop,
    isLast = false,
}: MeasurementSpineItemProps) {
    return (
        <div
            className={`obj-spine-item ${isLast ? 'obj-spine-item-end' : ''} relative`}
            style={{ '--obj-spine-dot-top': `${markerTop}px` } as CSSProperties}
        >
            <div
                className={'obj-spine-dot absolute items-center gap-[2px]'}
                aria-hidden
            >
                <div
                    className={'bg-obj-accent h-[16px] w-[16px] rounded-full border border-white'}
                />
                <div className={'bg-obj-accent h-px w-[31px]'} />
            </div>

            {children}
        </div>
    );
}
